
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OpeningBalanceModal from './components/OpeningBalanceModal';
import SupervisorPinModal, { PasswordType } from '../pos-checkout/components/SupervisorPinModal';
import OutletCheckoutModal from './components/OutletCheckoutModal';
import VerifySessionModal from './components/VerifySessionModal';
import { posApi } from '../../api/pos';
import { unwrapResponse } from '../../api/client';
import { authApi } from '../../api/auth';
import { useAuth } from '../../auth/AuthContext';
import { toast } from '../../components/toast';
import ProfileDropdown from '../../components/ProfileDropdown';
import { appendDrawerEvent } from '../../utils/drawerLedger';
import { toLocalISOString } from '../../utils/dateUtils';

interface Outlet {
  id: string;
  name: string;
  code: string;
  address: string;
  warehouseId?: number;
  warehouse?: {
    id?: number;
    name?: string;
  };
  isOccupiedByOtherUser?: boolean;
  accountBalance?: number;
  cashAmount?: number;
  cashAccountName?: string;
  remainingOpeningCash?: number;
  lastSessionInfo?: any;
}

interface Counter {
  id: string;
  name: string;
  status: 'available' | 'occupied' | 'maintenance';
  isActive?: boolean;
  lastSessionInfo?: any;
  userEmail?: string;
  hasActiveSession?: boolean;
  isActiveForCurrentUser?: boolean;
  outletId?: string;
}

export default function POSCheckIn() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const getUserFullName = () => {
    try {
      const profile = JSON.parse(localStorage.getItem('clientBusinessProfile') || '{}');
      return profile?.userName || user?.profile?.name || 'Cashier';
    } catch {
      return user?.profile?.name || 'Cashier';
    }
  };

  const getUserRole = () => {
    try {
      const profile = JSON.parse(localStorage.getItem('clientBusinessProfile') || '{}');
      return profile?.activeRoles?.[0]?.name || 'Cashier';
    } catch {
      return 'Cashier';
    }
  };
  const [selectedOutlet, setSelectedOutlet] = useState<string>('');
  const [selectedCounter, setSelectedCounter] = useState<string>('');
  const [selectedCounterData, setSelectedCounterData] = useState<any>(null);
  const [showOpeningBalance, setShowOpeningBalance] = useState(false);
  const [showCheckinPinModal, setShowCheckinPinModal] = useState(false);
  const [verifiedData, setVerifiedData] = useState<any>(null);
  const [availableCounters, setAvailableCounters] = useState<Counter[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  const [loadingCounters, setLoadingCounters] = useState(false);
  const [loadingOutletSession, setLoadingOutletSession] = useState(false);
  const [outletSessionInfo, setOutletSessionInfo] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [verifyingCounters, setVerifyingCounters] = useState<Record<string, boolean>>({});
  const [verifiedCounters, setVerifiedCounters] = useState<Set<string>>(new Set());
  const [checkingInOutlet, setCheckingInOutlet] = useState(false);
  const [outletActionError, setOutletActionError] = useState<string | null>(null);
  const [showOutletCheckoutModal, setShowOutletCheckoutModal] = useState(false);
  const [checkoutModalSession, setCheckoutModalSession] = useState<any>(null);
  const [verifyModalCounter, setVerifyModalCounter] = useState<Counter | null>(null);
  const [checkedInOutletId, setCheckedInOutletId] = useState<string | null>(null);
  const [pendingCheckIn, setPendingCheckIn] = useState<{ verifiedData: any; amount: number; note: string; denominations?: any } | null>(null);
  const [showCheckinConfirmModal, setShowCheckinConfirmModal] = useState(false);
  const [checkingInCounter, setCheckingInCounter] = useState(false);
  const [pendingCheckInCounterId, setPendingCheckInCounterId] = useState<string | null>(() => {
    try {
      const stored = sessionStorage.getItem('posCheckIn');
      if (stored) {
        const data = JSON.parse(stored);
        return String(data?.counter?.id ?? '') || null;
      }
    } catch {}
    return null;
  });
  const [showResumePinModal, setShowResumePinModal] = useState(false);
  const [pendingResumeCounter, setPendingResumeCounter] = useState<Counter | null>(null);
  const hasAutoSelectedRef = useRef(false);
  const selectedOutletRef = useRef<string>('');
  const hasOutletCheckinPermRef = useRef<boolean | null>(null);

  // Permissions
  const [hasOutletCheckinPerm, setHasOutletCheckinPerm] = useState<boolean | null>(null);
  const [hasCounterCheckinPerm, setHasCounterCheckinPerm] = useState<boolean | null>(null);
  const [counterOutletMap, setCounterOutletMap] = useState<Record<string, { outlet: Outlet; outletSession: any }>>({});;


  const mapOutlet = (outlet: any): Outlet => ({
    id: String(outlet?.id ?? outlet?.outletId ?? outlet?.OutletId ?? outlet?.code ?? outlet?.Code ?? ""),
    name: outlet?.name ?? outlet?.outletName ?? outlet?.Name ?? "Outlet",
    code: outlet?.code ?? outlet?.Code ?? "",
    address: outlet?.address ?? outlet?.Address ?? "",
    warehouseId: Number(outlet?.warehouse?.id ?? outlet?.warehouseId ?? 0) || undefined,
    warehouse: outlet?.warehouse
      ? {
          id: Number(outlet?.warehouse?.id ?? 0) || undefined,
          name: outlet?.warehouse?.name,
        }
      : undefined,
  });

  const mapCounter = (counter: any): Counter => {
    const currentUserIdRaw = localStorage.getItem("userId");
    const currentUserId = currentUserIdRaw ? Number(currentUserIdRaw) : null;
    const rawStatus = String(counter?.status ?? "").toLowerCase();
    const isActive = counter?.isActive !== false;
    const hasActiveSession =
      counter?.lastSessionInfo &&
      counter?.lastSessionInfo?.endTime == null;
    const isActiveForCurrentUser =
      hasActiveSession &&
      currentUserId != null &&
      Number(counter?.lastSessionInfo?.user?.id) === currentUserId;
    const status: Counter["status"] =
      rawStatus === "available" || rawStatus === "occupied" || rawStatus === "maintenance"
        ? (rawStatus as Counter["status"])
        : !isActive
        ? "maintenance"
        : hasActiveSession && !isActiveForCurrentUser
        ? "occupied"
        : "available";

    return {
      id: String(counter?.id ?? counter?.counterId ?? counter?.CounterId ?? counter?.code ?? counter?.Code ?? ""),
      name: counter?.name ?? counter?.counterName ?? counter?.Name ?? "Counter",
      status,
      isActive: !!counter?.isActive,
      lastSessionInfo: counter?.lastSessionInfo ?? null,
      userEmail: counter?.lastSessionInfo?.user?.email ?? counter?.userEmail ?? "",
      hasActiveSession,
      isActiveForCurrentUser,
      outletId: String(counter?.outletId ?? counter?.OutletId ?? counter?.outlet?.id ?? ''),
    };
  };

  const formatMoney = (value?: number | null) => {
    const amount = typeof value === "number" ? value : 0;
    return `${amount.toFixed(2)}`;
  };

  const computeRemainingOpeningCash = (sessionInfo: any, cashAmount: number) => {
    const totalUsedBalance = (sessionInfo?.balanceInfo ?? []).reduce(
      (acc: number, item: any) => acc + Number(item?.openingBalance ?? 0),
      0
    );
    const totalReceivedCash = Number(sessionInfo?.totalReceivedCash ?? 0);
    const openingCash = sessionInfo?.openingCash != null ? Number(sessionInfo.openingCash) : Number(cashAmount ?? 0);
    return openingCash - totalUsedBalance + totalReceivedCash;
  };

  // A counter can have multiple balanceInfo entries for the same outlet session
  // (one per check-in/checkout cycle) — the most recent one holds the current opening balance.
  const getLatestBalanceEntry = (balanceInfo: any[] | undefined, counterId: number, outletSessionId?: number | string) => {
    const matches = (balanceInfo ?? []).filter(
      (info: any) =>
        Number(info?.counterId) === counterId &&
        (outletSessionId == null || Number(info?.outletSessionId) === Number(outletSessionId))
    );
    return matches[matches.length - 1];
  };

  const getCurrentUserId = () => {
    const clientProfileRaw = localStorage.getItem("clientBusinessProfile");
    if (!clientProfileRaw) return null;
    try {
      const profile = JSON.parse(clientProfileRaw);
      return profile?.userId ?? profile?.UserId ?? null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const resolvePermission = (res: any) => {
      const data = unwrapResponse<any>(res);
      if (typeof data === 'boolean') return data;
      return data?.isAuthorized === true || data?.isAllowed === true;
    };
    Promise.all([
      authApi.checkPermission('OutletSessionCheckIn', 'PosOutletCheckin'),
      authApi.checkPermission('PosCashRegisterCheckIn', 'StoreTerminal'),
    ])
      .then(([outletRes, counterRes]) => {
        const outletPerm = resolvePermission(outletRes);
        const counterPerm = resolvePermission(counterRes);
        hasOutletCheckinPermRef.current = outletPerm;
        setHasOutletCheckinPerm(outletPerm);
        setHasCounterCheckinPerm(counterPerm);
      })
      .catch(() => {
        hasOutletCheckinPermRef.current = false;
        setHasOutletCheckinPerm(false);
        setHasCounterCheckinPerm(false);
      });
  }, []);

  const loadOutlets = async () => {
    setLoadingOutlets(true);
    setApiError(null);
    try {
      const response = await posApi.getOutlets({ isActive: true });
      const data = unwrapResponse<any[]>(response);
      if (Array.isArray(data) && data.length > 0) {
        const currentUserId = Number(localStorage.getItem("userId") ?? "");

        const mappedOutlets = data.map((o: any) => {
          const mapped = mapOutlet(o);
          const sessionUserId = Number(o.lastSessionInfo?.user?.id ?? 0);
          const sessionActive = o.lastSessionInfo?.endTime == null;
          mapped.isOccupiedByOtherUser =
            sessionActive && !!sessionUserId && sessionUserId !== currentUserId;
          mapped.accountBalance = typeof o.accountBalance === 'number' ? o.accountBalance : 0;
          mapped.cashAmount = typeof o.cashAmount === 'number' ? o.cashAmount : (typeof o.accountBalance === 'number' ? o.accountBalance : 0);
          mapped.cashAccountName = o.cashAccountName ?? null;
          mapped.lastSessionInfo = o.lastSessionInfo ?? null;
          mapped.remainingOpeningCash = computeRemainingOpeningCash(o.lastSessionInfo, mapped.cashAmount ?? 0);
          return mapped;
        });
        setOutlets(mappedOutlets);

        // Detect if current user has an active outlet session from API
        if (currentUserId) {
          const activeOutlet = data.find(
            (o: any) =>
              o.lastSessionInfo?.endTime == null &&
              Number(o.lastSessionInfo?.user?.id) === currentUserId
          );
          if (activeOutlet) {
            setCheckedInOutletId(String(activeOutlet.id));
          }
        }
      } else {
        setOutlets([]);
      }
    } catch (err) {
      setApiError("Failed to load outlets from backend.");
      setOutlets([]);
    } finally {
      setLoadingOutlets(false);
    }
  };

  // Load outlets on mount and whenever the user returns to this tab/window
  useEffect(() => {
    loadOutlets();

    const refetch = async () => {
      if (hasOutletCheckinPermRef.current === false) {
        await loadAllActiveCounters();
        return;
      }
      await loadOutlets();
      const outletId = selectedOutletRef.current;
      if (outletId) {
        const freshSession = await loadOutletSession(outletId);
        await loadCounters(outletId, freshSession);
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    const handleFocus = () => refetch();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Counter-only users: auto-load all counters once permissions resolve
  useEffect(() => {
    if (hasOutletCheckinPerm === false && hasCounterCheckinPerm === true) {
      loadAllActiveCounters();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOutletCheckinPerm, hasCounterCheckinPerm]);

  // When outlets finish loading, auto-select the checked-in outlet so the
  // "Select Counter" section appears without requiring another click.
  // Skip for counter-only users — they auto-load via loadAllActiveCounters.
  useEffect(() => {
    if (hasOutletCheckinPermRef.current === false) return;
    if (hasAutoSelectedRef.current || !checkedInOutletId || !outlets.length || selectedOutlet) return;
    if (!outlets.find((o) => o.id === checkedInOutletId)) return;
    hasAutoSelectedRef.current = true;
    handleOutletSelect(checkedInOutletId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets, checkedInOutletId]);

  const loadCounters = async (outletId: string, outletSession?: any, selectedCounterOverride: string = selectedCounter) => {
    setLoadingCounters(true);
    try {
      const response = await posApi.getCounters({
        outletId: Number(outletId),
        isActive: null,
      });
      const data = unwrapResponse<any[]>(response);
      if (Array.isArray(data)) {
        const isSessionActive = outletSession?.endTime == null;
        const mapped = data.map((counter) => {
          const mappedCounter = mapCounter(counter);
          if (!isSessionActive) {
            return {
              ...mappedCounter,
              hasActiveSession: false,
              isActiveForCurrentUser: false,
              status: mappedCounter.isActive ? "available" as const : "maintenance" as const,
            };
          }
          return mappedCounter;
        });
        setAvailableCounters(mapped);
        if (selectedCounterOverride) {
          const refreshed = mapped.find((item) => item.id === selectedCounterOverride);
          setSelectedCounterData(refreshed ?? null);
        } else {
          // Auto-select the counter the current user already has an open session on,
          // so the Resume Session card shows without an extra click.
          const activeForUser = mapped.find((item) => item.isActiveForCurrentUser);
          if (activeForUser) {
            setSelectedCounter(activeForUser.id);
            setSelectedCounterData(activeForUser);
          }
        }
        return mapped;
      } else {
        setAvailableCounters([]);
        return [];
      }
    } catch {
      setAvailableCounters([]);
      return [];
    } finally {
      setLoadingCounters(false);
    }
  };

  const loadOutletSession = async (outletId: string) => {
    setLoadingOutletSession(true);
    try {
      const sessionRes = await posApi.getLastOutletSession(Number(outletId));
      const sessionData = unwrapResponse<any>(sessionRes);
      setOutletSessionInfo(sessionData);
      localStorage.setItem("outletSessionInfo", JSON.stringify(sessionData));
      setOutlets(prev => prev.map(o => o.id === outletId ? {
        ...o,
        lastSessionInfo: sessionData,
        remainingOpeningCash: computeRemainingOpeningCash(sessionData, o.cashAmount ?? 0),
      } : o));
      return sessionData;
    } catch {
      setOutletSessionInfo(null);
      return null;
    } finally {
      setLoadingOutletSession(false);
    }
  };

  const loadAllActiveCounters = async () => {
    setLoadingCounters(true);
    setApiError(null);
    try {
      // Single call — all active counters across all outlets
      const counterRes = await posApi.getCounters({ isActive: true, outletId: null });
      const counterRaw = unwrapResponse<any[]>(counterRes);
      if (!Array.isArray(counterRaw)) { setAvailableCounters([]); return; }

      const allCounters = counterRaw.map((c) => mapCounter(c));
      setAvailableCounters(allCounters);

      // Unique outlet IDs from the counter data — matches mapCounter's fallback chain
      // so a nested `outlet.id` (instead of a flat outletId/OutletId) still resolves.
      const resolveRawOutletId = (c: any) => Number(c?.outletId ?? c?.OutletId ?? c?.outlet?.id ?? c?.Outlet?.id ?? 0);
      const outletIds = [...new Set(
        counterRaw.map(resolveRawOutletId).filter((id) => id > 0),
      )];

      // Load outlets list + sessions for each relevant outlet in parallel
      const [outletRes, ...sessionResults] = await Promise.all([
        posApi.getOutlets({ isActive: true }),
        ...outletIds.map((id) => posApi.getLastOutletSession(id).catch(() => null)),
      ]);

      const outletData = unwrapResponse<any[]>(outletRes) ?? [];
      const currentUserId = Number(localStorage.getItem('userId') ?? '');
      const mappedOutlets = outletData.map((o: any) => {
        const mapped = mapOutlet(o);
        const sessionUserId = Number(o.lastSessionInfo?.user?.id ?? 0);
        const sessionActive = o.lastSessionInfo?.endTime == null;
        mapped.isOccupiedByOtherUser = sessionActive && !!sessionUserId && sessionUserId !== currentUserId;
        mapped.accountBalance = typeof o.accountBalance === 'number' ? o.accountBalance : 0;
        mapped.cashAmount = typeof o.cashAmount === 'number' ? o.cashAmount : (typeof o.accountBalance === 'number' ? o.accountBalance : 0);
        mapped.cashAccountName = o.cashAccountName ?? null;
        mapped.lastSessionInfo = o.lastSessionInfo ?? null;
        mapped.remainingOpeningCash = computeRemainingOpeningCash(o.lastSessionInfo, mapped.cashAmount ?? 0);
        return mapped;
      });
      setOutlets(mappedOutlets);

      // Build counterOutletMap using the parallel session results
      const newMap: Record<string, { outlet: Outlet; outletSession: any }> = {};
      outletIds.forEach((outletId, idx) => {
        const outletSession = sessionResults[idx] ? unwrapResponse<any>(sessionResults[idx]) : null;
        if (!outletSession || outletSession.endTime != null) return;
        const outlet = mappedOutlets.find((o) => String(o.id) === String(outletId));
        if (!outlet) return;
        counterRaw.forEach((raw, i) => {
          if (resolveRawOutletId(raw) === outletId) {
            newMap[allCounters[i].id] = { outlet, outletSession };
          }
        });
      });
      setCounterOutletMap(newMap);

      // Auto-select if current user has an active counter session
      const activeCounter = allCounters.find((c) => c.isActiveForCurrentUser);
      if (activeCounter && newMap[activeCounter.id]) {
        const { outlet, outletSession } = newMap[activeCounter.id];
        setSelectedOutlet(outlet.id);
        selectedOutletRef.current = outlet.id;
        setCheckedInOutletId(outlet.id);
        setOutletSessionInfo(outletSession);
        localStorage.setItem('outletSessionInfo', JSON.stringify(outletSession));
        setSelectedCounter(activeCounter.id);
        setSelectedCounterData(activeCounter);
      }
    } catch {
      setApiError('Failed to load counters from backend.');
      setAvailableCounters([]);
    } finally {
      setLoadingCounters(false);
    }
  };

  // Handle outlet selection
  const handleOutletSelect = async (outletId: string) => {
    setSelectedOutlet(outletId);
    selectedOutletRef.current = outletId;
    setSelectedCounter('');
    setSelectedCounterData(null);
    setOutletActionError(null);
    setAvailableCounters([]);
    setOutletSessionInfo(null);
    const selectedOutletData = outlets.find((outlet) => outlet.id === outletId);
    if (selectedOutletData) {
      localStorage.setItem("outlet", JSON.stringify(selectedOutletData));
      const outletWarehouseId =
        Number(
          selectedOutletData?.warehouse?.id ?? selectedOutletData?.warehouseId ?? 0
        ) || 0;
      if (outletWarehouseId) {
        localStorage.setItem("warehouseId", String(outletWarehouseId));
      } else {
        localStorage.removeItem("warehouseId");
      }
    }

    let sessionData = await loadOutletSession(outletId);
    const isActive = sessionData && sessionData?.endTime == null;
    let checkInFailed = false;

    if (!isActive) {
      if (!hasOutletCheckinPerm) {
        toast.error("You don't have permission to check in to an outlet.");
        setSelectedOutlet('');
        selectedOutletRef.current = '';
        return;
      }
      setCheckingInOutlet(true);
      try {
        const userId = getCurrentUserId();
        const checkInPayload = {
          outletId: Number(outletId),
          userId,
          openingCash: outlets.find(o => o.id === outletId)?.accountBalance ?? 0,
          startTime: toLocalISOString(),
        };
        const checkInRes = await posApi.checkInOutlet(checkInPayload) as any;
        if (checkInRes?.succeeded === false) {
          const errorMsg = checkInRes?.errors?.[0] ?? checkInRes?.message ?? "Failed to check in outlet.";
          toast.error(errorMsg);
          setSelectedOutlet('');
          selectedOutletRef.current = '';
          checkInFailed = true;
        } else {
          const latestSession = await loadOutletSession(outletId);
          sessionData = latestSession;
        }
      } catch {
        toast.error("Failed to check in outlet. Please try again.");
        setSelectedOutlet('');
        selectedOutletRef.current = '';
        checkInFailed = true;
      } finally {
        setCheckingInOutlet(false);
      }
    }

    if (!checkInFailed) {
      const currentUserId = Number(localStorage.getItem("userId") ?? "");
      localStorage.setItem("pendingCheckedInOutlet", JSON.stringify({ outletId, userId: currentUserId }));
      setCheckedInOutletId(outletId);
      await loadCounters(outletId, sessionData);
    } else {
      setAvailableCounters([]);
    }
  };

  const handleStartShift = async () => {
    if (!selectedOutlet || !selectedCounterData) return;

    // Resume existing session for current user — require drawer PIN first.
    // Allowed regardless of hasOutletCheckinPerm since it only needs counter checkin permission.
    if (selectedCounterData?.isActiveForCurrentUser) {
      setShiftError(null);
      handleResumeSession(selectedCounterData);
      return;
    }

    if (!hasOutletCheckinPerm) {
      setShiftError("You don't have permission to activate a counter.");
      return;
    }

    setShiftError(null);

    try {
      if (!outletSessionInfo || outletSessionInfo?.endTime != null) {
        setShiftError("Outlet is not checked in. Please contact your outlet manager.");
        return;
      }

      const outletDetailsRes = await posApi.getOutletById(Number(selectedOutlet));
      const outletDetails = unwrapResponse<any>(outletDetailsRes);

      const counterLastSessionRes = await posApi.getLastCounterSession(
        Number(selectedCounterData.id)
      );
      const counterLastSession = unwrapResponse<any>(counterLastSessionRes);

      const currentOutlet = outlets.find(o => o.id === selectedOutlet);
      const outletAccountBalance = Number(currentOutlet?.cashAmount ?? currentOutlet?.accountBalance ?? 0);

      let openingCash = 0;
      if (
        counterLastSession?.outletSession?.id &&
        outletSessionInfo?.id &&
        counterLastSession.outletSession.id === outletSessionInfo.id
      ) {
        openingCash = counterLastSession?.closingCash ?? outletAccountBalance;
      } else {
        const balance = getLatestBalanceEntry(
          outletSessionInfo?.balanceInfo,
          Number(selectedCounterData.id),
          outletSessionInfo?.id
        );
        openingCash = Number(balance?.openingBalance ?? 0) || outletAccountBalance;
      }

      let currentUserName = "";
      let currentUserEmail = "";
      try {
        const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
        currentUserName = userInfo?.name ?? userInfo?.Name ?? "";
        currentUserEmail = userInfo?.email ?? userInfo?.Email ?? "";
      } catch {
        currentUserName = "";
      }

      const checkInData = {
        outlet: currentOutlet,
        outletDetails,
        counter: selectedCounterData,
        outletSession: outletSessionInfo,
        counterLastSession,
        openingCash,
        remainingBalance: computeRemainingOpeningCash(outletSessionInfo, outletAccountBalance),
        user: {
          name: currentUserName,
          email: currentUserEmail,
        },
        timestamp: toLocalISOString(),
      };
      setVerifiedData(checkInData);
      setShowOpeningBalance(true);
    } catch (err) {
      setShiftError("Failed to check in with backend. Please try again.");
    }
  };

  const handleOpeningBalanceConfirm = async (
    amount: number,
    note: string,
    denominations?: any
  ) => {
    const remainingBalance = Number(verifiedData?.remainingBalance ?? verifiedData?.outlet?.cashAmount ?? verifiedData?.outlet?.accountBalance ?? 0);
    if (amount > remainingBalance) {
      toast.error('Sorry! counter opening cash amount exceeds outlet opening cash amount!');
      return;
    } else if (amount == null || amount < 0) {
      toast.error('Sorry! enter valid amount!');
      return;
    }

    try {
      if (!verifiedData?.counter?.isActive) {
        await posApi.changeCounterActiveStatus({ counterId: Number(verifiedData?.counter?.id), isActive: true });
      }
      await posApi.addCounterOpeningBalance({
        counterId: Number(verifiedData?.counter?.id),
        outletSessionId: Number(verifiedData?.outletSession?.id),
        openingBalance: amount,
        previousBalance: Number(verifiedData?.counterLastSession?.closingCash ?? 0),
      });
    } catch {
      setShiftError("Failed to set opening balance. Please try again.");
      return;
    }

    const completeCheckInData = {
      ...verifiedData,
      openingBalance: { amount, note, denominations, timestamp: toLocalISOString() },
    };
    sessionStorage.setItem('posCheckIn', JSON.stringify(completeCheckInData));
    sessionStorage.setItem('posOpeningBalance', JSON.stringify({ amount, note, timestamp: toLocalISOString() }));

    setPendingCheckIn({ verifiedData: { ...verifiedData }, amount, note, denominations });
    setShowOpeningBalance(false);
    setVerifiedData(null);

    // Counter-only users go straight to PIN — no card-based Check In step
    if (hasOutletCheckinPermRef.current === false) {
      setShowCheckinPinModal(true);
      return;
    }

    setPendingCheckInCounterId(String(verifiedData.counter.id));
    setSelectedCounter('');
    setSelectedCounterData(null);

    const freshSession = await loadOutletSession(selectedOutlet);
    await loadCounters(selectedOutlet, freshSession, '');

    toast.success('Opening balance set. Click "Check In" on the counter to start your session.');
  };

  const handleFinalCheckIn = async () => {
    if (!pendingCheckIn) return;
    const { verifiedData: pd, amount, note } = pendingCheckIn;

    // Block check-in if user already has an active session on a different counter
    const activeCounterForUser = availableCounters.find(c => c.isActiveForCurrentUser);
    if (activeCounterForUser && activeCounterForUser.id !== String(pd?.counter?.id)) {
      setShiftError(`You already have an active session on "${activeCounterForUser.name}". Please check out from that counter first.`);
      return;
    }

    setCheckingInCounter(true);

    const clientProfileRaw = localStorage.getItem("clientBusinessProfile");
    let currentUserId: number | null = null;
    if (clientProfileRaw) {
      try {
        const profile = JSON.parse(clientProfileRaw);
        currentUserId = profile?.userId ?? profile?.UserId ?? null;
      } catch {
        currentUserId = null;
      }
    }

    let outletSessionId = pd?.outletSession?.id;
    if (!outletSessionId) {
      try {
        const lastCounterSessionRes = await posApi.getLastCounterSession(Number(pd?.counter?.id));
        const lastCounterSession = unwrapResponse<any>(lastCounterSessionRes);
        outletSessionId = lastCounterSession?.outletSession?.id;
      } catch { /* ignore */ }
    }

    if (!outletSessionId) {
      const errorMsg = "Outlet is not checked in. Please contact your outlet manager.";
      toast.error(errorMsg);
      setShiftError(errorMsg);
      setCheckingInCounter(false);
      return;
    }

    const entity = {
      counterId: Number(pd?.counter?.id),
      userId: currentUserId,
      openingCash: amount,
      startTime: toLocalISOString(),
      outletSessionId,
    };

    try {
      const checkInRes = await posApi.checkInCounter(entity) as any;
      if (checkInRes?.succeeded === false) {
        const errorMsg = checkInRes?.errors?.[0] ?? checkInRes?.message ?? "Failed to open counter session.";
        toast.error(errorMsg);
        setShiftError(errorMsg);
        setCheckingInCounter(false);
        return;
      }
      const sessionId = unwrapResponse<number>(checkInRes);
      const counterSessionRes = await posApi.getCounterSessionInfo(sessionId);
      const counterSessionInfo = unwrapResponse<any>(counterSessionRes);

      localStorage.setItem("counterCheckedInInfo", JSON.stringify({ outletName: pd?.outlet?.name ?? null, counterName: pd?.counter?.name ?? null }));
      localStorage.setItem("counterSession", JSON.stringify(counterSessionInfo ?? null));
      localStorage.setItem("counter", JSON.stringify(pd?.counter ?? null));
      localStorage.setItem("outlet", JSON.stringify(pd?.outlet ?? null));
      localStorage.setItem("outletSession", JSON.stringify(pd?.outletSession ?? null));
      const checkedInWarehouseId = Number(pd?.outletDetails?.warehouse?.id ?? pd?.outlet?.warehouse?.id ?? pd?.outlet?.warehouseId ?? 0) || 0;
      if (checkedInWarehouseId) localStorage.setItem("warehouseId", String(checkedInWarehouseId));
    } catch {
      setShiftError("Failed to open counter session. Please try again.");
      setCheckingInCounter(false);
      return;
    }

    appendDrawerEvent({
      type: 'opening_balance',
      amount,
      reason: 'Shift opening balance',
      note,
      user: pd?.user?.name || 'Cashier',
      role: 'Cashier',
    });

    setCheckingInCounter(false);
    setShowCheckinConfirmModal(false);
    setPendingCheckIn(null);
    setPendingCheckInCounterId(null);
    sessionStorage.removeItem('posCheckIn');
    sessionStorage.removeItem('posOpeningBalance');
    navigate('/pos-checkout');
  };

  const handleOpeningBalanceCancel = () => {
    setShowOpeningBalance(false);
    setVerifiedData(null);
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'occupied':
        return 'bg-red-500';
      case 'maintenance':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'occupied':
        return 'Occupied';
      case 'maintenance':
        return 'Maintenance';
      default:
        return '';
    }
  };

  const isOutletSessionActive =
    outletSessionInfo && outletSessionInfo?.endTime == null;

  const selectedCounterNeedsVerification =
    selectedCounterData?.isActive === true &&
    !!selectedCounterData?.lastSessionInfo &&
    !selectedCounterData?.hasActiveSession &&
    !selectedCounterData?.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') &&
    !verifiedCounters.has(selectedCounterData?.id);

  const isCounterActiveAndVerified =
    !!selectedCounterData &&
    selectedCounterData?.isActive === true &&
    !selectedCounterData?.isActiveForCurrentUser &&
    (
      selectedCounterData?.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') ||
      verifiedCounters.has(selectedCounterData?.id)
    );

  const canStartShift =
    !!selectedOutlet &&
    !!selectedCounterData &&
    isOutletSessionActive &&
    !selectedCounterNeedsVerification &&
    !isCounterActiveAndVerified &&
    (
      selectedCounterData?.isActiveForCurrentUser ||
      !(selectedCounterData?.hasActiveSession && selectedCounterData?.isActive)
    );

  // Outlet checkout is allowed when no counter is currently active AND
  // all counters with unverified ended sessions (no sessionStatus) have been verified
  const hasAnyActiveCounterSession = availableCounters.some(c => c.hasActiveSession);
  const allSessionsVerified =
    !hasAnyActiveCounterSession &&
    availableCounters
      .filter(c => c.isActive && c.lastSessionInfo && !c.hasActiveSession && !c.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif'))
      .every(c => verifiedCounters.has(c.id));

  const filteredCounters = availableCounters.filter(counter =>
    hasOutletCheckinPerm !== false ||
    !(counter.lastSessionInfo?.sessionStatus?.id === 0 &&
      counter.lastSessionInfo?.sessionStatus?.name === 'Open') ||
    counter.isActiveForCurrentUser
  );

  const handleVerifySession = (counter: Counter) => {
    if (!counter.lastSessionInfo?.id) return;
    if (!hasOutletCheckinPerm) {
      toast.error("You don't have permission to verify sessions.");
      return;
    }
    setVerifyModalCounter(counter);
  };

  const handleVerifySessionConfirm = async (transferedAmount: number) => {
    const counter = verifyModalCounter;
    if (!counter) return;
    const counterId = Number(counter.id);
    const session = counter.lastSessionInfo;

    setVerifyingCounters(prev => ({ ...prev, [counter.id]: true }));
    try {
      const payload = {
        counterId,
        counterSessionId: session.id,
        transferedAmount,
        cashIn: session.cashIn ?? 0,
        cashOut: session.cashOut ?? 0,
        salesReturnAmount: session.totalSalesReturnAmount ?? session.salesReturnAmount ?? 0,
        salesAmount: session.totalSalesAmount ?? session.salesAmount ?? 0,
        cashShortOrExcess: session.cashShortOrExcess ?? 0,
        receivedBankAmount: session.totalReceivedBankAmount ?? session.receivedBankAmount ?? 0,
        salesRefundAmount: session.totalSalesRefundAmount ?? session.salesRefundAmount ?? 0,
        adjustedCreditAmount: session.totalAdjustedCreditAmount ?? session.adjustedCreditAmount ?? 0,
      };
      await posApi.verifyCounterSession(payload);
      toast.success(`Session verified for ${counter.name}.`);
      setVerifyModalCounter(null);
      setVerifiedCounters(prev => new Set([...prev, counter.id]));
      if (pendingCheckInCounterId === counter.id) {
        setPendingCheckIn(null);
        setPendingCheckInCounterId(null);
        sessionStorage.removeItem('posCheckIn');
        sessionStorage.removeItem('posOpeningBalance');
      }
      await Promise.all([
        loadCounters(selectedOutlet!),
        loadOutletSession(selectedOutlet!),
      ]);
    } catch {
      setVerifyingCounters(prev => ({ ...prev, [counter.id]: false }));
      toast.error("Failed to verify session. Please try again.");
      throw new Error("Failed to verify session. Please try again.");
    } finally {
      setVerifyingCounters(prev => ({ ...prev, [counter.id]: false }));
    }
  };

  const handleActivateCounter = (counter: Counter) => {
    setSelectedCounter(counter.id);
    setSelectedCounterData(counter);
  };

  const handleResumeSession = (counter: Counter) => {
    setPendingResumeCounter(counter);
    setShowResumePinModal(true);
  };

  const performResumeSession = async (counter: Counter) => {
    const outletEntry = counterOutletMap[counter.id];
    let currentOutlet = outletEntry?.outlet ?? outlets.find(o => o.id === selectedOutlet);
    let currentOutletSession = outletEntry?.outletSession ?? outletSessionInfo;
    const resolvedOutletId = outletEntry?.outlet?.id ?? counter.outletId ?? selectedOutlet ?? '';
    const currentOutletId = String(resolvedOutletId);

    if (outletEntry) {
      setSelectedOutlet(outletEntry.outlet.id);
      selectedOutletRef.current = outletEntry.outlet.id;
      setOutletSessionInfo(outletEntry.outletSession);
      setCheckedInOutletId(outletEntry.outlet.id);
    } else if (currentOutletId) {
      setSelectedOutlet(currentOutletId);
      selectedOutletRef.current = currentOutletId;
      setCheckedInOutletId(currentOutletId);
    }

    try {
      const counterLastSessionRes = await posApi.getLastCounterSession(Number(counter.id));
      const counterLastSession = unwrapResponse<any>(counterLastSessionRes);
      const sessionId = counterLastSession?.id;
      let counterSessionInfo = counterLastSession;
      if (sessionId) {
        const counterSessionRes = await posApi.getCounterSessionInfo(sessionId);
        counterSessionInfo = unwrapResponse<any>(counterSessionRes) ?? counterLastSession;
      }
      if (!currentOutletSession && currentOutletId) {
        currentOutletSession = await loadOutletSession(currentOutletId);
        currentOutlet = outlets.find(o => String(o.id) === currentOutletId) ?? currentOutlet;
      }
      const outletDetailsRes = await posApi.getOutletById(Number(currentOutletId));
      const outletDetails = unwrapResponse<any>(outletDetailsRes);
      localStorage.setItem("counterCheckedInInfo", JSON.stringify({ outletName: currentOutlet?.name ?? null, counterName: counter.name }));
      localStorage.setItem("counterSession", JSON.stringify(counterSessionInfo ?? null));
      localStorage.setItem("counter", JSON.stringify(counter ?? null));
      localStorage.setItem("outlet", JSON.stringify(currentOutlet ?? null));
      localStorage.setItem("outletSession", JSON.stringify(currentOutletSession ?? null));
      const warehouseId = Number(outletDetails?.warehouse?.id ?? currentOutlet?.warehouse?.id ?? currentOutlet?.warehouseId ?? 0) || 0;
      if (warehouseId) localStorage.setItem("warehouseId", String(warehouseId));
      navigate("/pos-checkout");
    } catch {
      toast.error("Failed to resume session. Please try again.");
    }
  };

  const handleVerifiedCounterCheckIn = async (counter: Counter) => {
    if (!hasCounterCheckinPerm) return;
    setShiftError(null);

    const outletEntry = counterOutletMap[counter.id];
    let currentOutlet = outletEntry?.outlet ?? outlets.find(o => o.id === selectedOutlet);
    let currentOutletSession = outletEntry?.outletSession ?? outletSessionInfo;
    const resolvedOutletId = outletEntry?.outlet?.id ?? counter.outletId ?? selectedOutlet ?? '';
    const currentOutletId = String(resolvedOutletId);

    // Update UI selection state
    setSelectedCounter(counter.id);
    setSelectedCounterData(counter);
    if (outletEntry) {
      setSelectedOutlet(outletEntry.outlet.id);
      selectedOutletRef.current = outletEntry.outlet.id;
      setOutletSessionInfo(outletEntry.outletSession);
      setCheckedInOutletId(outletEntry.outlet.id);
    } else if (currentOutletId) {
      setSelectedOutlet(currentOutletId);
      selectedOutletRef.current = currentOutletId;
      setCheckedInOutletId(currentOutletId);
    }

    let currentUserName = "";
    let currentUserEmail = "";
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      currentUserName = userInfo?.name ?? userInfo?.Name ?? "";
      currentUserEmail = userInfo?.email ?? userInfo?.Email ?? "";
    } catch { /* ignore */ }

    // Counter-only users: skip the opening-balance modal, go straight to PIN,
    // but still carry over the real opening cash from the last balance entry.
    if (!hasOutletCheckinPerm) {
      // Always fetch fresh outlet/counter session info on click — cached
      // counterOutletMap/outletSessionInfo can be stale and wrongly report
      // the outlet as not checked in.
      if (currentOutletId) {
        currentOutletSession = await loadOutletSession(currentOutletId);
        currentOutlet = outlets.find(o => String(o.id) === currentOutletId) ?? currentOutlet;
      }
      if (!currentOutletSession || currentOutletSession?.endTime != null) {
        toast.error("Outlet is not checked in. Please contact your outlet manager.");
        return;
      }
      let counterLastSession = null;
      try {
        const counterLastSessionRes = await posApi.getLastCounterSession(Number(counter.id));
        counterLastSession = unwrapResponse<any>(counterLastSessionRes);
      } catch { /* ignore */ }

      const outletBalance = Number(currentOutlet?.cashAmount ?? currentOutlet?.accountBalance ?? 0);
      let openingCash = 0;
      if (
        counterLastSession?.outletSession?.id &&
        currentOutletSession?.id &&
        counterLastSession.outletSession.id === currentOutletSession.id
      ) {
        openingCash = counterLastSession?.closingCash ?? outletBalance;
      } else {
        const balance = getLatestBalanceEntry(
          currentOutletSession?.balanceInfo,
          Number(counter.id),
          currentOutletSession?.id
        );
        openingCash = Number(balance?.openingBalance ?? 0) || outletBalance;
      }

      setPendingCheckIn({
        verifiedData: {
          outlet: currentOutlet,
          outletDetails: null,
          counter,
          outletSession: currentOutletSession,
          counterLastSession,
          openingCash,
          user: { name: currentUserName, email: currentUserEmail },
          timestamp: toLocalISOString(),
        },
        amount: openingCash,
        note: '',
        denominations: undefined,
      });
      setShowCheckinPinModal(true);
      return;
    }

    // Outlet managers: fetch session if needed, then carry over the already-known
    // opening cash (from the verified transfer or last balance entry) straight to
    // check-in — this counter is already verified, so don't re-collect the amount.
    if ((!currentOutletSession || currentOutletSession?.endTime != null) && currentOutletId) {
      currentOutletSession = await loadOutletSession(currentOutletId);
      currentOutlet = outlets.find(o => String(o.id) === currentOutletId) ?? currentOutlet;
    }
    if (!currentOutletSession || currentOutletSession?.endTime != null) {
      toast.error("Outlet is not checked in. Please contact your outlet manager.");
      return;
    }
    try {
      const outletDetailsRes = await posApi.getOutletById(Number(currentOutletId));
      const outletDetails = unwrapResponse<any>(outletDetailsRes);
      const counterLastSessionRes = await posApi.getLastCounterSession(Number(counter.id));
      const counterLastSession = unwrapResponse<any>(counterLastSessionRes);
      const outletBalance = Number(currentOutlet?.cashAmount ?? currentOutlet?.accountBalance ?? 0);
      let openingCash = 0;
      if (counterLastSession?.outletSession?.id && currentOutletSession?.id &&
          counterLastSession.outletSession.id === currentOutletSession.id) {
        openingCash = counterLastSession?.closingCash ?? outletBalance;
      } else {
        const balance = getLatestBalanceEntry(
          currentOutletSession?.balanceInfo,
          Number(counter.id),
          currentOutletSession?.id
        );
        openingCash = Number(balance?.openingBalance ?? 0) || outletBalance;
      }
      setPendingCheckIn({
        verifiedData: {
          outlet: currentOutlet,
          outletDetails,
          counter,
          outletSession: currentOutletSession,
          counterLastSession,
          openingCash,
          user: { name: currentUserName, email: currentUserEmail },
          timestamp: toLocalISOString(),
        },
        amount: openingCash,
        note: '',
        denominations: undefined,
      });
      setShowCheckinPinModal(true);
    } catch {
      toast.error("Failed to start check-in. Please try again.");
    }
  };

  const handleOutletCheckout = async () => {
    if (!selectedOutlet || !outletSessionInfo) return;
    if (!hasOutletCheckinPerm) {
      toast.error("You don't have permission to check out an outlet.");
      return;
    }
    setOutletActionError(null);

    const outletId = Number(selectedOutlet);
    if (Number.isNaN(outletId)) {
      setOutletActionError("Invalid outlet selected.");
      return;
    }

    try {
      const anyActiveRes = await posApi.anyCounterIsActive(outletId);
      const anyActive = unwrapResponse<any>(anyActiveRes);
      if (anyActive === true) {
        const latestCounters = await loadCounters(selectedOutlet, outletSessionInfo);
        const hasActiveInUi = latestCounters.some((counter) => counter.isActive);
        if (hasActiveInUi) {
          setOutletActionError("To check out, all counters must be inactive.");
          return;
        }
      }
    } catch {
      setOutletActionError("Failed to verify counters. Please try again.");
      return;
    }

    // Fetch latest realtime session data before opening modal
    const freshSession = await loadOutletSession(selectedOutlet);
    setCheckoutModalSession(freshSession);
    setShowOutletCheckoutModal(true);
  };

  const handleOutletCheckoutConfirm = async (expectedClosingCash: number, cashShortOrExcess: number) => {
    if (!selectedOutlet || !outletSessionInfo) return;
    const outletId = Number(selectedOutlet);
    const userId = getCurrentUserId();
    const session = outletSessionInfo ?? {};

    const payload = {
      id: session?.id ?? null,
      outletId,
      userId,
      openingCash: Number(session?.openingCash ?? 0),
      totalCashIn: Number(session?.totalCashIn ?? session?.cashIn ?? 0),
      totalCashOut: Number(session?.totalCashOut ?? session?.cashOut ?? 0),
      totalReceivedCash: Number(session?.totalReceivedCash ?? 0),
      totalReceivedBankAmount: Number(session?.totalReceivedBankAmount ?? 0),
      totalSalesReturnAmount: Number(session?.totalSalesReturnAmount ?? 0),
      totalSalesRefundAmount: Number(session?.totalSalesRefundAmount ?? 0),
      totalAdjustedCreditAmount: Number(session?.totalAdjustedCreditAmount ?? 0),
      totalSalesAmount: Number(session?.totalSalesAmount ?? 0),
      closingCash: expectedClosingCash,
      cashShortOrExcess,
      startTime: session?.startTime ?? toLocalISOString(),
      endTime: toLocalISOString(),
    };

    await posApi.checkOutOutlet(payload);

    setShowOutletCheckoutModal(false);
    toast.success("Outlet checked out successfully.");
    localStorage.removeItem("warehouseId");
    localStorage.removeItem("pendingCheckedInOutlet");
    setCheckedInOutletId(null);
    setSelectedOutlet('');
    selectedOutletRef.current = '';
    setSelectedCounter('');
    setSelectedCounterData(null);
    setAvailableCounters([]);
    setOutletSessionInfo(null);
    setVerifiedCounters(new Set());
    hasAutoSelectedRef.current = false;
    await loadOutlets();
  };


  return (
    <div className="min-h-screen relative p-8">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(https://static.readdy.ai/image/82828e1ff69cacd1e8315694d834201a/02729a0bd7704f1310dd99c4c0c28996.png)' }}
      >
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">POS Check-In</h1>
            <p className="text-gray-600 text-sm">{hasOutletCheckinPerm === false ? 'Select your counter to begin your shift' : 'Select your outlet and counter to begin your shift'}</p>
            {apiError && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 inline-block">
                {apiError}
              </p>
            )}
          </div>
          <ProfileDropdown
            name={getUserFullName()}
            role={getUserRole()}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Outlet & Counter Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Select Outlet — hidden for counter-only users */}
            {hasOutletCheckinPerm !== false && <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-store-3-line text-xl text-gray-700"></i>
                <h2 className="text-lg font-semibold text-gray-900">Select Outlet</h2>
              </div>
              <div className="space-y-3">
                {loadingOutlets && (
                  <div className="text-sm text-gray-500">Loading outlets...</div>
                )}
                {!loadingOutlets && outlets.length === 0 && (
                  <div className="text-sm text-gray-500">No outlets found.</div>
                )}
                {outlets.map(outlet => {
                  const isLockedOut = checkedInOutletId !== null && checkedInOutletId !== outlet.id;
                  const isDisabled = isLockedOut || !!outlet.isOccupiedByOtherUser;
                  const disabledTitle = isLockedOut
                    ? 'You are already checked in to another outlet'
                    : outlet.isOccupiedByOtherUser
                    ? 'Another user is active in this outlet'
                    : undefined;
                  // Counter data is only loaded for the selected outlet, so reserved
                  // counter cash can only be subtracted accurately for that card.
                  // Only verified counters have their closing cash reserved — an
                  // unverified session's closing cash isn't confirmed yet.
                  const reservedCounterCash = outlet.id === selectedOutlet
                    ? availableCounters
                        .filter(
                          counter =>
                            verifiedCounters.has(counter.id) ||
                            (counter.lastSessionInfo as any)?.sessionStatus?.name?.toLowerCase().includes('verif')
                        )
                        .reduce(
                          (sum, counter) => sum + Number((counter.lastSessionInfo as any)?.closingCash ?? 0),
                          0
                        )
                    : 0;
                  const remainingBalance = (outlet.remainingOpeningCash ?? 0) - reservedCounterCash;
                  return (
                  <button
                    key={outlet.id}
                    onClick={() => !isDisabled && handleOutletSelect(outlet.id)}
                    disabled={isDisabled}
                    title={disabledTitle}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      isDisabled
                        ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                        : selectedOutlet === outlet.id
                        ? 'border-blue-500 bg-blue-50 cursor-pointer'
                        : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-line text-lg text-gray-700"></i>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm truncate">{outlet.name}</h3>
                          <p className="text-xs text-gray-500 truncate">{outlet.address}</p>
                          {outlet.isOccupiedByOtherUser && (
                            <p className="text-[10px] text-red-500">Another user is active</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-[11px] w-[30rem]">
                          <div className="row-start-1 col-start-1 min-w-0">
                            <span className="text-gray-400">Last User: </span>
                            <span className="font-medium text-blue-600 truncate">{outlet.lastSessionInfo?.user?.email || '—'}</span>
                          </div>
                          {outlet.cashAccountName && (
                            <div className="row-start-1 col-start-2">
                              <span className="text-gray-400">Cash Account: </span>
                              <span className="font-medium text-gray-700">{outlet.cashAccountName}</span>
                            </div>
                          )}
                          {!outlet.lastSessionInfo?.endTime && (
                            <div className="row-start-1 col-start-3">
                              <span className="text-gray-400">Opening Cash: </span>
                              <span className="font-medium text-gray-700">{formatMoney(outlet.lastSessionInfo?.openingCash)}</span>
                            </div>
                          )}
                          <div className="row-start-2 col-start-1">
                            <span className="text-gray-400">Last Closing Cash: </span>
                            <span className="font-medium text-gray-700">{formatMoney(outlet.lastSessionInfo?.closingCash)}</span>
                          </div>
                          <div className="row-start-2 col-start-2">
                            <span className="text-gray-400">Cash Amount: </span>
                            <span className="font-medium text-gray-700">{formatMoney(outlet.cashAmount)}</span>
                          </div>
                          {!outlet.lastSessionInfo?.endTime && outlet.accountBalance != null && outlet.id === checkedInOutletId && (
                            <div className="row-start-2 col-start-3">
                              <span className="text-gray-400">Remaining Balance: </span>
                              <span className="font-medium text-gray-700">{formatMoney(remainingBalance)}</span>
                            </div>
                          )}
                        </div>
                        {selectedOutlet === outlet.id && !isDisabled && (
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-sm text-white"></i>
                          </div>
                        )}
                        {isDisabled && (
                          <i className="ri-lock-line text-gray-300 text-base flex-shrink-0"></i>
                        )}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>}

            {/* Hint when outlet is checked in but no counter selected */}
            {!selectedCounter && availableCounters.length > 0 && !loadingCounters && (selectedOutlet || hasOutletCheckinPerm === false) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <i className="ri-cursor-line text-blue-500 text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-sm font-medium text-blue-900">Select a counter to start your shift</p>
                  <p className="text-xs text-blue-700 mt-0.5">Click on a counter card below to select it, then start or resume your session.</p>
                </div>
              </div>
            )}

            {/* Select Counter */}
            {(selectedOutlet || hasOutletCheckinPerm === false) && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-computer-line text-xl text-gray-700"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Select Counter</h2>
                </div>
                {hasOutletCheckinPerm !== false && (
                  <>
                    {loadingOutletSession && (
                      <div className="mb-3 text-xs text-gray-500">Loading outlet session info...</div>
                    )}
                    {outletSessionInfo && (
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs text-gray-600">
                          Last Session: {outletSessionInfo?.sessionCode || "N/A"}
                        </span>
                        {isOutletSessionActive && hasOutletCheckinPerm && (
                          <button
                            type="button"
                            onClick={handleOutletCheckout}
                            disabled={!allSessionsVerified}
                            title={!allSessionsVerified ? 'Verify all counter sessions before checking out' : undefined}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                              allSessionsVerified
                                ? 'bg-teal-500 hover:bg-teal-600 text-white cursor-pointer'
                                : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                            }`}
                          >
                            Outlet Check Out
                          </button>
                        )}
                      </div>
                    )}
                    {outletSessionInfo && !isOutletSessionActive && (
                      <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        Outlet is checked out. Selecting the outlet will check it in.
                      </div>
                    )}
                    {checkingInOutlet && (
                      <div className="mb-3 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                        Checking in outlet...
                      </div>
                    )}
                    {outletActionError && (
                      <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                        {outletActionError}
                      </div>
                    )}
                  </>
                )}
<div className="grid grid-cols-2 gap-3">
                  {loadingCounters && (
                    <div className="col-span-2 text-sm text-gray-500">Loading counters...</div>
                  )}
                  {!loadingCounters && filteredCounters.length === 0 && (
                    <div className="col-span-2 text-sm text-gray-500 text-center py-4">
                      No active counter is available. Please contact your outlet manager.
                    </div>
                  )}
                  {filteredCounters.map(counter => (
                    <div
                      key={counter.id}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button')) return;
                        // An active counter (not the current user's own session) is selectable
                        // via its inline "Check In" button, not by selecting the card — selecting
                        // it here would hide that button for no reason.
                        if ((counter.status !== 'occupied' || counter.isActiveForCurrentUser) && (!counter.isActive || counter.isActiveForCurrentUser)) {
                          setSelectedCounter(counter.id);
                          setSelectedCounterData(counter);
                          if (hasOutletCheckinPerm === false && counterOutletMap[counter.id]) {
                            const { outlet, outletSession } = counterOutletMap[counter.id];
                            setSelectedOutlet(outlet.id);
                            selectedOutletRef.current = outlet.id;
                            setOutletSessionInfo(outletSession);
                            setCheckedInOutletId(outlet.id);
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          if ((counter.status !== 'occupied' || counter.isActiveForCurrentUser) && (!counter.isActive || counter.isActiveForCurrentUser)) {
                            setSelectedCounter(counter.id);
                            setSelectedCounterData(counter);
                            if (hasOutletCheckinPerm === false && counterOutletMap[counter.id]) {
                              const { outlet, outletSession } = counterOutletMap[counter.id];
                              setSelectedOutlet(outlet.id);
                              selectedOutletRef.current = outlet.id;
                              setOutletSessionInfo(outletSession);
                              setCheckedInOutletId(outlet.id);
                            }
                          }
                        }
                      }}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        selectedCounter === counter.id
                          ? 'border-blue-500 bg-blue-50'
                          : counter.status === 'occupied' && !counter.isActiveForCurrentUser
                          ? 'border-red-200 bg-red-50 cursor-not-allowed opacity-60'
                          : counter.isActive && !counter.isActiveForCurrentUser && hasCounterCheckinPerm === true
                          ? 'border-gray-200 bg-white cursor-default'
                          : counter.status === 'occupied' && counter.isActiveForCurrentUser
                          ? 'border-blue-200 bg-blue-50 cursor-pointer'
                          : !counter.isActive
                          ? 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                          : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                      }`}
                    >
                      {selectedCounter === counter.id && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-xs text-white"></i>
                        </div>
                      )}

                      <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <i className="ri-computer-line text-lg text-gray-700"></i>
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-gray-900 text-sm">{counter.name}</p>
                              {hasOutletCheckinPerm === false && counterOutletMap[counter.id] && (
                                <p className="text-[10px] text-gray-400">{counterOutletMap[counter.id].outlet.name}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    counter.isActive
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {counter.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-gray-600 space-y-1">
                          <div className="flex items-center justify-between">
                            <span>Last Closing Time:</span>
                            <span className="font-medium text-gray-800">
                              {counter.lastSessionInfo?.endTime
                                ? new Date(counter.lastSessionInfo.endTime).toLocaleString()
                                : '—'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Last Closing Cash:</span>
                            <span className="font-medium text-gray-800">
                              {formatMoney(counter.lastSessionInfo?.closingCash)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Last User:</span>
                            <span className="font-medium text-blue-600">
                              {counter.userEmail || '—'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Cash short or excess:</span>
                            <span className="font-medium text-gray-800">
                              {formatMoney(counter.lastSessionInfo?.cashShort ?? counter.lastSessionInfo?.cashShortOrExcess)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Transferred Amount:</span>
                            <span className="font-medium text-gray-800">
                              {formatMoney(counter.lastSessionInfo?.transferedAmount)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {counter.lastSessionInfo?.sessionStatus?.name && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${counter.lastSessionInfo.sessionStatus.name.toLowerCase() === 'closed' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {counter.lastSessionInfo.sessionStatus.name}
                              </span>
                            )}
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {counter.isActive && counter.lastSessionInfo && !counter.hasActiveSession && !verifiedCounters.has(counter.id) && !counter.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') && hasOutletCheckinPerm && (
                              verifyingCounters[counter.id] ? (
                                <span className="px-3 py-1 rounded-full text-[10px] font-semibold border border-gray-200 text-gray-400">
                                  Verifying...
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVerifySession(counter);
                                  }}
                                  className="px-3 py-1 rounded-full text-[10px] font-semibold border border-amber-400 text-amber-700 hover:bg-amber-50 cursor-pointer"
                                >
                                  Verify Session
                                </button>
                              )
                            )}
                            {!counter.isActive && hasOutletCheckinPerm && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActivateCounter(counter);
                                }}
                                className="px-3 py-1 rounded-full text-[10px] font-semibold border border-green-400 text-green-700 hover:bg-green-50 cursor-pointer"
                              >
                                Activate
                              </button>
                            )}
                            {(() => {
                              // Resolve THIS counter's own outlet session — counterOutletMap carries the
                              // right session per-outlet for the bulk (counter-only) list; outletSessionInfo
                              // is only a valid fallback in the single-selected-outlet (outlet-manager) flow.
                              const resolvedSession = counterOutletMap[counter.id]?.outletSession ?? outletSessionInfo;
                              const balanceEntry = getLatestBalanceEntry(
                                resolvedSession?.balanceInfo,
                                Number(counter.id),
                                resolvedSession?.id
                              );
                              const needsVerification = counter.isActive && counter.lastSessionInfo && !counter.hasActiveSession && !verifiedCounters.has(counter.id) && !counter.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif');
                              // A counter that has never had a session (lastSessionInfo null) is unambiguous —
                              // an unconsumed balanceEntry means it's ready, regardless of which browser/tab set it.
                              const isFreshCounter = counter.lastSessionInfo == null;
                              return counter.isActive && !!balanceEntry && !counter.hasActiveSession && !needsVerification && selectedCounterData?.id !== counter.id && (isFreshCounter || String(counter.id) === pendingCheckInCounterId);
                            })() && hasCounterCheckinPerm && (
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const outletEntry = counterOutletMap[counter.id];
                                  const resolvedOutletId = outletEntry?.outlet?.id ?? counter.outletId ?? selectedOutlet ?? '';
                                  let resolvedSession = outletEntry?.outletSession ?? outletSessionInfo;
                                  let resolvedOutlet = outletEntry?.outlet ?? outlets.find(o => o.id === selectedOutlet);
                                  // Always fetch fresh outlet/counter session info on click — cached
                                  // state can be stale and wrongly report the outlet as not checked in.
                                  if (resolvedOutletId) {
                                    resolvedSession = await loadOutletSession(String(resolvedOutletId));
                                    resolvedOutlet = outlets.find(o => String(o.id) === String(resolvedOutletId)) ?? resolvedOutlet;
                                  }
                                  if (!resolvedSession || resolvedSession?.endTime != null) {
                                    toast.error("Outlet is not checked in. Please contact your outlet manager.");
                                    return;
                                  }
                                  let counterLastSession = null;
                                  try {
                                    const counterLastSessionRes = await posApi.getLastCounterSession(Number(counter.id));
                                    counterLastSession = unwrapResponse<any>(counterLastSessionRes);
                                  } catch { /* ignore */ }
                                  const balanceEntry = getLatestBalanceEntry(
                                    resolvedSession?.balanceInfo,
                                    Number(counter.id),
                                    resolvedSession?.id
                                  );
                                  setSelectedCounter('');
                                  setSelectedCounterData(null);
                                  setPendingCheckIn({
                                    verifiedData: {
                                      counter,
                                      outlet: resolvedOutlet,
                                      outletSession: resolvedSession,
                                      counterLastSession,
                                      outletDetails: null,
                                      user: { name: '', email: '' },
                                    },
                                    amount: Number(balanceEntry?.openingBalance ?? 0),
                                    note: '',
                                    denominations: undefined,
                                  });
                                  setShowCheckinPinModal(true);
                                }}
                                className="px-3 py-1 rounded-full text-[10px] font-semibold bg-green-600 text-white hover:bg-green-700 cursor-pointer"
                              >
                                Check In
                              </button>
                            )}
                            {hasCounterCheckinPerm === true &&
                              counter.isActive &&
                              !counter.hasActiveSession &&
                              (
                                (counter.lastSessionInfo == null && !getLatestBalanceEntry(
                                  (counterOutletMap[counter.id]?.outletSession ?? outletSessionInfo)?.balanceInfo,
                                  Number(counter.id),
                                  (counterOutletMap[counter.id]?.outletSession ?? outletSessionInfo)?.id
                                )) ||
                                counter.lastSessionInfo?.sessionStatus?.name?.toLowerCase().includes('verif') ||
                                verifiedCounters.has(counter.id)
                              ) &&
                              String(counter.id) !== pendingCheckInCounterId &&
                              (
                                selectedCounterData?.id !== counter.id ||
                                (counter.lastSessionInfo == null && hasCounterCheckinPerm)
                              ) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerifiedCounterCheckIn(counter);
                                }}
                                className="px-3 py-1 rounded-full text-[10px] font-semibold bg-green-600 text-white hover:bg-green-700 cursor-pointer"
                              >
                                Check In
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Right Column - Start Shift - Show when any counter is selected, but not for an already-active counter (that flow uses the inline "Check In" button on the card instead). Counter-only users (hasOutletCheckinPerm === false) only get this panel to resume their own active session. */}
          {selectedCounter && !!selectedCounterData && (
            hasOutletCheckinPerm !== false
              ? (!selectedCounterData.isActive || selectedCounterData.isActiveForCurrentUser)
              : selectedCounterData.isActiveForCurrentUser
          ) && (
            <div className="lg:col-span-1">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm sticky top-8 outline-none">
                <div className="flex items-center gap-2 mb-4">
                  <i className={`ri-${selectedCounterData?.isActiveForCurrentUser ? 'refresh-line' : 'play-circle-line'} text-xl text-gray-700`}></i>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedCounterData?.isActiveForCurrentUser ? 'Resume Session' : 'Start Shift'}
                  </h2>
                </div>

                <div className="mb-6 space-y-3 text-sm text-gray-600">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">Outlet</span>
                    <span className="font-medium text-gray-900">
                      {outlets.find(o => o.id === selectedOutlet)?.name || ''}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-500">Counter</span>
                    <span className="font-medium text-gray-900">
                      {selectedCounterData?.name || ''}
                    </span>
                  </div>
                </div>

                {shiftError && (
                  <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {shiftError}
                  </div>
                )}

                {/* Continue Button */}
                {(hasOutletCheckinPerm || selectedCounterData?.isActiveForCurrentUser) && (
                <button
                  onClick={handleStartShift}
                  disabled={!canStartShift}
                  title={selectedCounterNeedsVerification ? 'Please verify the previous session before activating this counter' : undefined}
                  className={`w-full py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                    !canStartShift
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                  }`}
                >
                  <i className={`ri-${selectedCounterData?.isActiveForCurrentUser ? 'refresh-line' : 'checkbox-circle-line'} text-lg`}></i>
                  {selectedCounterData?.isActiveForCurrentUser ? 'Resume Session' : 'Activate Counter & Set Opening Balance'}
                </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCheckinPinModal && (
        <SupervisorPinModal
          title="Counter Check-In"
          subtitle="Enter PIN to complete check-in"
          passwordType={PasswordType.Drawer}
          counterId={Number(pendingCheckIn?.verifiedData?.counter?.id) || 0}
          onSuccess={() => { setShowCheckinPinModal(false); handleFinalCheckIn(); }}
          onClose={() => setShowCheckinPinModal(false)}
        />
      )}

      {showResumePinModal && (
        <SupervisorPinModal
          title="Resume Session"
          subtitle="Enter PIN to resume your session"
          passwordType={PasswordType.Drawer}
          counterId={Number(pendingResumeCounter?.id) || 0}
          onSuccess={() => {
            setShowResumePinModal(false);
            if (pendingResumeCounter) performResumeSession(pendingResumeCounter);
            setPendingResumeCounter(null);
          }}
          onClose={() => { setShowResumePinModal(false); setPendingResumeCounter(null); }}
        />
      )}

      {showOpeningBalance && verifiedData && (
        <OpeningBalanceModal
          onConfirm={handleOpeningBalanceConfirm}
          onCancel={handleOpeningBalanceCancel}
          cashierName={(() => { try { const p = JSON.parse(localStorage.getItem('clientBusinessProfile') || '{}'); return p?.userName || user?.profile?.name || verifiedData.user?.name || ''; } catch { return user?.profile?.name || verifiedData.user?.name || ''; } })()}
          storeName={verifiedData.outlet?.name || ''}
          lane={verifiedData.counter?.name || ''}
          maxAmount={Number(verifiedData.remainingBalance ?? verifiedData.outlet?.cashAmount ?? verifiedData.outlet?.accountBalance ?? 0)}
        />
      )}


      {verifyModalCounter && (() => {
        const verifyOutletSession = counterOutletMap[verifyModalCounter.id]?.outletSession ?? outletSessionInfo;
        const previousBalanceEntry = getLatestBalanceEntry(
          verifyOutletSession?.balanceInfo,
          Number(verifyModalCounter.id),
          verifyOutletSession?.id
        );
        const previousCash = Number(previousBalanceEntry?.previousBalance ?? 0);
        return (
          <VerifySessionModal
            counterName={verifyModalCounter.name}
            session={verifyModalCounter.lastSessionInfo}
            previousCash={previousCash}
            onClose={() => setVerifyModalCounter(null)}
            onConfirm={handleVerifySessionConfirm}
          />
        );
      })()}

      {showOutletCheckoutModal && (checkoutModalSession ?? outletSessionInfo) && (() => {
        const session = checkoutModalSession ?? outletSessionInfo ?? {};
        const outletOpeningCash = Number(session?.openingCash ?? 0);
        const sales = Number(session?.totalSalesAmount ?? 0);
        const cashIn = Number(session?.totalCashIn ?? session?.cashIn ?? 0);
        const cashOut = Number(session?.totalCashOut ?? session?.cashOut ?? 0);
        const bankMobile = Number(session?.totalReceivedBankAmount ?? 0);
        const salesReturn = Number(session?.totalSalesReturnAmount ?? 0);
        const totalCounterOpeningCash = (session?.balanceInfo ?? []).reduce(
          (sum: number, item: any) => sum + Number(item?.openingBalance ?? 0),
          0
        );
        const selectedOutletData = outlets.find(o => o.id === selectedOutlet);
        const reservedCounterCash = availableCounters.reduce(
          (sum, counter) => sum + Number((counter.lastSessionInfo as any)?.closingCash ?? 0),
          0
        );
        const remainingCash = outletOpeningCash - totalCounterOpeningCash - reservedCounterCash;
        const cashAmount = Number(session?.totalReceivedCash ?? 0);
        const refund = Number(session?.totalSalesRefundAmount ?? 0);
        const creditApplied = Number(session?.totalAdjustedCreditAmount ?? 0);
        const closingCash = remainingCash + cashAmount;
        const accountableCash = remainingCash + cashAmount + reservedCounterCash;
        const outletName = selectedOutletData?.name ?? 'Outlet';

        return (
          <OutletCheckoutModal
            onClose={() => setShowOutletCheckoutModal(false)}
            onConfirm={async (_counted, _acceptDifference, difference) => {
              try {
                await handleOutletCheckoutConfirm(closingCash, difference);
              } catch {
                toast.error("Failed to check out outlet. Please try again.");
                throw new Error("Failed to check out outlet. Please try again.");
              }
            }}
            sessionId={session?.id}
            outletName={outletName}
            outletOpeningCash={outletOpeningCash}
            remainingCash={remainingCash}
            totalCounterOpeningCash={totalCounterOpeningCash}
            sales={sales}
            cashIn={cashIn}
            cashOut={cashOut}
            cashAmount={cashAmount}
            bankMobile={bankMobile}
            salesReturn={salesReturn}
            refund={refund}
            creditApplied={creditApplied}
            reservedCounterCash={reservedCounterCash}
            accountableCash={accountableCash}
            closingCash={closingCash}
          />
        );
      })()}
    </div>
  );
}



